import { CollideEvent, CollideId } from '../../defs/collide';
import {
  despawnBlockEntity,
  NestBlockEntity,
} from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import { getMatch2AssetPath } from '../../util/assets';
import { BeeGoalEffect } from '../effects/BeeGoalEffect';
import { BeeScatteringEffect } from '../effects/BeeScatteringEffect';
import { InvalidEffect } from '../effects/InvalidEffect';
import { SmokeEffect } from '../effects/SmokeEffect';
import { CollideHandler, ICollision } from './ICollision';

/*
    nest block collision resolver
*/
export class NestCollision implements ICollision {
  // fields
  //-------------------------------------------------------------------------
  // input
  private readonly _scene: GameScene;
  private readonly _entity: NestBlockEntity;
  // map
  private readonly _resolvers: { [key in CollideId]?: CollideHandler } = {
    attack: this._resolveAttack,
    block: this._resolveBlock,
    complete: this._resolveComplete,
    tap: this._resolveTap,
  };

  // init
  //-------------------------------------------------------------------------
  constructor(scene: GameScene, entity: NestBlockEntity) {
    this._scene = scene;
    this._entity = entity;
  }

  // impl
  //-------------------------------------------------------------------------
  public async resolve(event: CollideEvent): Promise<boolean> {
    return this._resolvers[event.id]?.call(this, event) ?? false;
  }

  // private: resolvers
  //-------------------------------------------------------------------------
  private async _resolveTap(event: CollideEvent) {
    // invalid effect
    await new InvalidEffect(this._entity).execute();
    return false;
  }

  private async _resolveBlock(event: CollideEvent) {
    // trigger nest
    await this._trigger();
    return true;
  }

  private async _resolveAttack(event: CollideEvent) {
    // trigger nest
    await this._trigger();
    return true;
  }

  private async _resolveComplete(event: CollideEvent) {
    const entity = this._entity;
    const position = entity.c.position.mapPosition;

    // despawn on complete
    despawnBlockEntity(this._scene, this._entity);

    await Promise.all([
      new SmokeEffect(this._scene, { position, tint: 0xfff7d7 }).execute(),
      new BeeScatteringEffect(this._scene, {
        position,
        asset: getMatch2AssetPath('fx.nest/fx.nest.bee.png'),
      }).execute(),
    ]);

    return true;
  }

  // private: actions
  //-------------------------------------------------------------------------
  private async _trigger() {
    const entity = this._entity;
    const position = entity.c.position.mapPosition;

    // Play bee hive splash fx
    entity.c.blockNest.view.playSpawnFx();

    // Animate bee flying to goal panel
    await new BeeGoalEffect(this._scene, {
      origin: entity.c.blockNest.view,
      position,
    }).execute();

    // trigger goal
    this._scene.sessionSystem.triggerGoal('nest', this._entity);
  }
}
